import { Component } from '@angular/core';
import {NgIf} from "@angular/common";

import {SubscriptionService} from "../../services/subscription.service";
import {BillingService} from "../../services/billing.service";
import {Subject} from "rxjs";
import {Store} from "@ngrx/store";
import {AppState} from "../../state/reducers";
import {takeUntil} from "rxjs/operators";
import * as UiActions from '../../state/ui/ui.actions';
import {selectIsSubscriptionBannerVisible} from "../../state/ui/ui.selectors";

@Component({
  selector: 'app-subscription-banner',
  standalone: true,
  imports: [
    NgIf
  ],
  templateUrl: './subscription-banner.component.html',
  styleUrl: './subscription-banner.component.scss'
})
export class SubscriptionBannerComponent {

  private destroy$ = new Subject<void>();

  user: any;
  subscription: any = { on_trial: false, subscribed: false, credits: 0 };
  showBanner: boolean = true;
  private notDismissed: boolean = true;

  constructor(
    private subscriptionService: SubscriptionService,
    private billingService: BillingService,
    private store: Store<AppState>
  ) {}

  ngOnInit(): void {
    // Check if banner was dismissed (localStorage) before subscribing
    this.checkBannerVisibility();

    // Subscribe to subscription data
    this.subscriptionService.subscriptionData()
      .pipe(takeUntil(this.destroy$))
      .subscribe(subscription => {
        this.subscription = subscription;
        this.updateBannerVisibility();
      });

    // Subscribe to the banner visibility state from the store
    this.store.select(selectIsSubscriptionBannerVisible)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isVisible => {
        this.notDismissed = isVisible;
        this.updateBannerVisibility();
      });
  }

  /**
   * Banner shows when: user is NOT subscribed AND banner was not dismissed.
   * Banner hides when: user IS subscribed OR banner was dismissed.
   */
  private updateBannerVisibility(): void {
    this.showBanner = this.notDismissed && !this.subscription?.subscribed;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openBillingPortal() {
    this.billingService.openBillingPortal();
  }

  closeBanner() {
    // Store the timestamp when the banner was closed
    const closedTimestamp = new Date().getTime();
    localStorage.setItem('subscriptionBannerClosedAt', closedTimestamp.toString());

    // Update the store
    this.store.dispatch(UiActions.toggleSubscriptionBanner({ isVisible: false }));
  }

  private checkBannerVisibility(): void {
    const closedAtString = localStorage.getItem('subscriptionBannerClosedAt');

    if (closedAtString) {
      const closedAt = parseInt(closedAtString, 10);
      const now = new Date().getTime();
      const oneDayInMs = 24 * 60 * 60 * 1000; // 1 day in milliseconds

      // Check if one day has passed since the banner was closed
      if (now - closedAt < oneDayInMs) {
        // Less than a day has passed, keep the banner closed
        this.store.dispatch(UiActions.toggleSubscriptionBanner({ isVisible: false }));
      } else {
        // More than a week has passed, show the banner again
        localStorage.removeItem('subscriptionBannerClosedAt');
        this.store.dispatch(UiActions.toggleSubscriptionBanner({ isVisible: true }));
      }
    }
  }

}
